// ============================================================
// Hirafi — Page /devis
//
// Mode invité  : formulaire de création + aperçu + PDF
// Mode connecté: liste des devis + création
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { Plus, FileText, Eye, ArrowLeft, Download, Share2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { StandaloneDevisBuilder } from "@/features/devis/components/StandaloneDevisBuilder";
import { DevisPreviewPrint } from "@/features/devis/components/DevisPreviewPrint";
import { GuestSavePrompt } from "@/features/devis/components/GuestSavePrompt";
import { useGuestDevis } from "@/features/devis/hooks/useGuestDevis";
import { useStandaloneQuotes } from "@/features/devis/hooks/useStandaloneQuotes";
import { clearGuestDraft, loadGuestDraft } from "@/features/devis/lib/storage";
import { getPublicDevisUrl, getWhatsappShareUrl } from "@/features/devis/lib/publicUrl";
import type { StandaloneDevisFormData } from "@/features/devis/types";
import { formatAmount } from "@/features/devis/lib/calculations";
import { toast } from "sonner";

type View = "list" | "create" | "preview";

// ── Statuts ────────────────────────────────────────────────
const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  draft:     { label: "Brouillon",  className: "bg-muted text-muted-foreground" },
  sent:      { label: "Envoyé",     className: "bg-blue-100 text-blue-700" },
  viewed:    { label: "Vu",         className: "bg-purple-100 text-purple-700" },
  accepted:  { label: "Accepté",    className: "bg-green-100 text-green-700" },
  rejected:  { label: "Refusé",     className: "bg-red-100 text-red-700" },
  cancelled: { label: "Annulé",     className: "bg-orange-100 text-orange-700" },
  expired:   { label: "Expiré",     className: "bg-gray-100 text-gray-500" },
};

// ── Composant principal ────────────────────────────────────
export default function DevisPage() {
  const { locale } = useLanguage("common");
  useSEO({
    title: "Créer un devis gratuit — Hirafi",
    description: locale === "ar"
      ? "أنشئ فاتورة تقديرية مجانية في دقائق وشاركها مع عملائك. أداة مجانية للحرفيين في الجزائر."
      : "Créez un devis gratuit en quelques minutes et partagez-le avec vos clients. Outil gratuit pour les artisans en Algérie.",
    canonical: "/devis",
    locale,
  });
  const { user, loading: authLoading } = useAuth();
  const navigate = useLocalizedNavigate();
  const [view, setView] = useState<View>(user ? "list" : "create");
  const [previewData, setPreviewData] = useState<StandaloneDevisFormData | null>(null);
  const [savedQuoteId, setSavedQuoteId] = useState<string | null>(null);
  const [guestActionTarget, setGuestActionTarget] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const { draft, update: updateDraft } = useGuestDevis();
  const { quotes, saving, saveQuote, reload } = useStandaloneQuotes();

  // ── Auto-save du brouillon invité après login ───────────
  // Déclenché une seule fois quand l'user vient de se connecter
  // et qu'un brouillon guest existe dans localStorage.
  const autoSaveRef = useRef(false);
  useEffect(() => {
    if (!user || autoSaveRef.current) return;
    const guestDraft = loadGuestDraft();
    if (!guestDraft?.title) return; // brouillon vide ou inexistant
    autoSaveRef.current = true;
    saveQuote(guestDraft).then((id) => {
      if (id) {
        clearGuestDraft();
        setSavedQuoteId(id);
        reload();
      }
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) return null;

  // ── Impression / PDF ────────────────────────────────────
  const handlePrint = () => {
    const prevTitle = document.title;
    document.title = previewData ? `Devis — ${previewData.title}` : "Devis — Hirafi";
    document.body.classList.add("printing-devis");
    window.print();
    const cleanup = () => {
      document.title = prevTitle;
      document.body.classList.remove("printing-devis");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
  };

  const handleCopyLink = (token: string) => {
    const url = getPublicDevisUrl(token);
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copié !"));
  };

  // ── Mode connecté : sauvegarde ─────────────────────────
  const handleSave = async (data: StandaloneDevisFormData) => {
    const id = await saveQuote(data);
    if (id) {
      clearGuestDraft();
      setSavedQuoteId(id);
      setView("list");
      reload();
    }
  };

  // ── Aperçu ─────────────────────────────────────────────
  const handlePreview = (data: StandaloneDevisFormData) => {
    setPreviewData(data);
    if (!user) updateDraft(data); // persist guest draft
    setView("preview");
  };

  // ── Render ─────────────────────────────────────────────

  // Vue : aperçu + PDF
  if (view === "preview" && previewData) {
    return (
      <div className="min-h-screen bg-background">
        {/* Barre d'actions (masquée à l'impression) */}
        <div className="no-print sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => setView(user ? "list" : "create")}
            >
              <ArrowLeft className="h-4 w-4" />
              Modifier
            </Button>
            <div className="flex items-center gap-2">
              {!user && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setGuestActionTarget("envoyer le devis")}
                >
                  <Share2 className="h-4 w-4" />
                  Envoyer
                </Button>
              )}
              <Button
                size="sm"
                variant="hero"
                className="gap-1.5"
                onClick={handlePrint}
              >
                <Download className="h-4 w-4" />
                Télécharger PDF
              </Button>
              {user && (
                <Button
                  size="sm"
                  variant="default"
                  className="gap-1.5"
                  onClick={() => handleSave(previewData)}
                  disabled={saving}
                >
                  <FileText className="h-4 w-4" />
                  {saving ? "Sauvegarde…" : "Sauvegarder"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Bannière invité */}
        {!user && guestActionTarget && (
          <div className="no-print max-w-3xl mx-auto px-4 pt-4">
            <GuestSavePrompt action={guestActionTarget} />
          </div>
        )}

        {/* Document PDF */}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <DevisPreviewPrint ref={printRef} data={previewData} />
        </div>
      </div>
    );
  }

  // Vue : formulaire de création
  if (view === "create") {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="mb-8">
            {user && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 mb-4 -ml-2"
                onClick={() => setView("list")}
              >
                <ArrowLeft className="h-4 w-4" />
                Mes devis
              </Button>
            )}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold">Créer un devis</h1>
                {!user && (
                  <p className="text-sm text-muted-foreground">
                    Gratuit · Sans inscription
                  </p>
                )}
              </div>
            </div>
          </div>

          <StandaloneDevisBuilder
            initialData={draft}
            onPreview={handlePreview}
            onSave={user ? handleSave : undefined}
            saving={saving}
            isGuest={!user}
          />

          {/* CTA invité sous le formulaire */}
          {!user && (
            <div className="mt-6">
              <GuestSavePrompt />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vue : liste (mode connecté uniquement)
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl font-bold">Mes devis</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {quotes.length} devis standalone
            </p>
          </div>
          <Button variant="hero" className="gap-2" onClick={() => setView("create")}>
            <Plus className="h-4 w-4" />
            Nouveau devis
          </Button>
        </div>

        {/* Notification devis sauvegardé */}
        {savedQuoteId && (
          <div className="mb-6 rounded-xl border border-accent/20 bg-accent/5 p-4 text-sm text-accent-foreground flex items-center gap-2">
            <span className="text-accent">✅</span>
            Devis sauvegardé avec succès.
          </div>
        )}

        {/* Liste */}
        {quotes.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucun devis encore créé.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setView("create")}
            >
              Créer mon premier devis
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => {
              const statusInfo = STATUS_LABELS[q.status] ?? STATUS_LABELS.draft;
              const publicUrl = q.public_token ? getPublicDevisUrl(q.public_token) : null;
              return (
                <div
                  key={q.id}
                  className="rounded-xl border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:border-border/80 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm truncate">{q.title}</p>
                      <Badge
                        variant="secondary"
                        className={`text-xs shrink-0 ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>
                        {q.client_name || "Client non renseigné"}
                      </span>
                      <span>·</span>
                      <span className="font-semibold text-primary">
                        {formatAmount(q.total_amount)}
                      </span>
                      <span>·</span>
                      <span>
                        {new Date(q.created_at).toLocaleDateString("fr-DZ")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {publicUrl && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 text-xs h-8"
                          onClick={() => handleCopyLink(q.public_token)}
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Lien
                        </Button>
                        {q.client_phone && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 text-xs h-8 text-green-700 border-green-200 hover:bg-green-50"
                            asChild
                          >
                            <a
                              href={getWhatsappShareUrl(q.public_token, q.client_phone ?? "")}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              WhatsApp
                            </a>
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 text-xs h-8"
                      onClick={() => navigate(`/devis/${q.id}`)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Voir
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
